import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const MODES = [
  { key: "even_then_winner", label: "Test, then send most traffic to the winner",
    hint: "Even split until every page has the test visits, then about 90% goes to the page with the most form submissions per visit." },
  { key: "gradual", label: "Shift toward the leader gradually",
    hint: "Starts even and moves more visits to the better page as the numbers come in. Never fully stops testing." },
];
const VISIT_OPTIONS = [50, 100, 200, 500];
const LABEL = "text-[10px] uppercase tracking-widest font-bold";

export function RotatorForm({ pages, initial, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [picked, setPicked] = useState(initial ? initial.variants.map((v) => v.slug) : []);
  const [mode, setMode] = useState(initial?.mode || "even_then_winner");
  const [minVisits, setMinVisits] = useState(initial?.min_visits || 100);
  const [saving, setSaving] = useState(false);
  const toggle = (slug) => setPicked((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  const visitOptions = [...new Set([...VISIT_OPTIONS, Number(initial?.min_visits || 100)])].sort((a, b) => a - b);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { name, pages: picked, mode, min_visits: Number(minVisits) };
    try {
      const { data } = initial ? await api.patch(`/vendor/rotators/${initial.id}`, body) : await api.post("/vendor/rotators", body);
      toast.success(initial ? "Rotating link updated" : "Rotating link created");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border-2 border-black bg-zinc-50 p-3 flex flex-col gap-3" data-testid="rotator-form">
      <label className="flex flex-col gap-1"><span className={LABEL}>Link name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} minLength={2} maxLength={60} required placeholder="Facebook – auto accident"
               className="border-2 border-black px-2 py-1.5 text-sm bg-white" data-testid="rotator-form-name" />
      </label>
      <fieldset className="flex flex-col gap-1"><legend className={`${LABEL} mb-1`}>Pages to rotate (pick 2–6)</legend>
        {pages.length < 2 && <div className="text-xs text-amber-800" data-testid="rotator-form-few-pages">You need at least 2 live approved pages. Ask your account manager to approve more.</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {pages.map((p) => (
            <label key={p.slug} className="flex items-center gap-2 border border-black bg-white px-2 py-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={picked.includes(p.slug)} onChange={() => toggle(p.slug)} disabled={!picked.includes(p.slug) && picked.length >= 6} data-testid={`rotator-form-page-${p.slug}`} />
              <span className="font-bold">{p.name}</span><span className="font-mono text-zinc-500 truncate">{p.slug}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-1.5"><legend className={`${LABEL} mb-1`}>How traffic moves after testing</legend>
        {MODES.map((m) => (
          <label key={m.key} className={`flex items-start gap-2 border-2 px-2 py-1.5 text-xs cursor-pointer ${mode === m.key ? "border-black bg-white" : "border-zinc-300 bg-white/60"}`}>
            <input type="radio" name="rotator-mode" checked={mode === m.key} onChange={() => setMode(m.key)} className="mt-0.5" data-testid={`rotator-form-mode-${m.key}`} />
            <span><b>{m.label}</b><span className="block text-zinc-600">{m.hint}</span></span>
          </label>
        ))}
      </fieldset>
      <label className="flex flex-wrap items-center gap-2 text-xs"><span className={LABEL}>Test visits per page</span>
        <select value={minVisits} onChange={(e) => setMinVisits(e.target.value)} className="border-2 border-black px-2 py-1 bg-white" data-testid="rotator-form-min-visits">
          {visitOptions.map((n) => <option key={n} value={n}>{n} visits</option>)}
        </select>
        <span className="text-zinc-500">traffic is split evenly until each page reaches this</span>
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={saving || picked.length < 2} data-testid="rotator-form-save"
                className="border-2 border-black bg-black text-white px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
          {saving ? "Saving…" : initial ? "Save changes" : "Create link"}
        </button>
        <button type="button" onClick={onCancel} data-testid="rotator-form-cancel"
                className="border-2 border-black bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-zinc-100">Cancel</button>
      </div>
    </form>
  );
}
